'use client';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useWallets } from '@privy-io/react-auth';
import { useQueryClient } from '@tanstack/react-query';
import { createWalletClient, custom, encodeFunctionData, erc20Abi } from 'viem';
import { polygon } from 'viem/chains';

import { useAuth, useProxyInitialization } from '@/hooks';
import { publicClient } from '@/utils/approvals.utils';

import { MESSAGES } from '@/constants/messages.constants';
import {
  NATIVE_USDC_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

const MIN_MIGRATE_AMOUNT = 0.001; // ignore dust < $0.001
/** Poll every 30s to catch delayed deposits from exchanges/card providers. */
const POLL_INTERVAL_MS = 30_000;

/**
 * useEmbeddedWalletMigration
 *
 * Transfers USDC.e and native USDC from the Privy embedded wallet (EOA) to the
 * proxy wallet (Gnosis Safe) so funds are usable for Polymarket CLOB trading.
 *
 * Why this is needed:
 * Privy's fundWallet always deposits into the user's embedded wallet, not into
 * any arbitrary address. Funds in the EOA are NOT tradeable — the Polymarket CLOB
 * only debits from the proxy wallet (funderAddress). This hook bridges that gap.
 *
 * Gas: The user's Privy embedded wallet must hold a small amount of POL (MATIC)
 * to cover the gas fee for this transfer on Polygon. If the wallet has no POL,
 * the migration fails and the user is prompted to top up their Privy wallet.
 */
export function useEmbeddedWalletMigration() {
  const { user } = useAuth();
  const { wallets } = useWallets();
  const { syncBalance } = useProxyInitialization();
  const queryClient = useQueryClient();
  const isMigratingRef = useRef(false);

  const migrateToProxy = useCallback(async () => {
    const proxyAddress = user?.proxyWalletAddress;

    if (!proxyAddress || isMigratingRef.current) return;

    // Always migrate from the Privy embedded wallet — that's where Privy's card
    // and exchange payments land regardless of what address fundWallet is called with.
    // If the user has no embedded wallet (MetaMask-only login), skip — funds already
    // went to the proxy directly.
    const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
    if (!embeddedWallet) {
      console.debug(
        '[EmbeddedMigration] No embedded wallet found — skipping (MetaMask-only user).'
      );
      return;
    }

    const eoaAddress = embeddedWallet.address;

    isMigratingRef.current = true;
    try {
      // Create a wallet client specifically from the embedded wallet's provider.
      // We CANNOT use the signer from usePolymarketClient — for email+MetaMask users
      // that signer is the MetaMask wallet, which cannot sign for the embedded wallet's
      // private key. Only Privy's own provider has access to the embedded wallet.
      const provider = await embeddedWallet.getEthereumProvider();
      const embeddedSigner = createWalletClient({
        account: eoaAddress as `0x${string}`,
        chain: polygon,
        transport: custom(provider),
      });

      const [usdceRaw, nativeUsdcRaw, polBalanceWei] = await Promise.all([
        publicClient.readContract({
          address: USDC_E_CONTRACT_ADDRESS as `0x${string}`,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [eoaAddress as `0x${string}`],
          authorizationList: undefined,
        }),
        publicClient.readContract({
          address: NATIVE_USDC_CONTRACT_ADDRESS as `0x${string}`,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [eoaAddress as `0x${string}`],
          authorizationList: undefined,
        }),
        publicClient.getBalance({ address: eoaAddress as `0x${string}` }),
      ]);

      const usdceAmount = Number(usdceRaw) / 1e6;
      const nativeAmount = Number(nativeUsdcRaw) / 1e6;
      const polAmount = Number(polBalanceWei) / 1e18;

      if (usdceAmount < MIN_MIGRATE_AMOUNT && nativeAmount < MIN_MIGRATE_AMOUNT) {
        console.debug('[EmbeddedMigration] Balance below threshold — nothing to migrate.');
        return;
      }

      // toast.info(MESSAGES.TOAST.MOVING_FUNDS);

      const txs: Promise<`0x${string}`>[] = [];

      if (usdceAmount >= MIN_MIGRATE_AMOUNT) {
        txs.push(
          embeddedSigner.sendTransaction({
            to: USDC_E_CONTRACT_ADDRESS as `0x${string}`,
            data: encodeFunctionData({
              abi: erc20Abi,
              functionName: 'transfer',
              args: [proxyAddress as `0x${string}`, usdceRaw as bigint],
            }),
            // chain is already set on the wallet client; omit here to satisfy viem types
          } as any)
        );
      }

      if (nativeAmount >= MIN_MIGRATE_AMOUNT) {
        txs.push(
          embeddedSigner.sendTransaction({
            to: NATIVE_USDC_CONTRACT_ADDRESS as `0x${string}`,
            data: encodeFunctionData({
              abi: erc20Abi,
              functionName: 'transfer',
              args: [proxyAddress as `0x${string}`, nativeUsdcRaw as bigint],
            }),
          } as any)
        );
      }

      const txHashes = await Promise.all(txs);

      // Wait for Polygon block confirmation (~3s) before calling updateBalanceAllowance.
      // The CLOB reads on-chain state — calling too early returns the old balance.
      await new Promise((r) => setTimeout(r, 4000));

      // Sync CLOB so the new proxy balance is tradeable immediately.
      await syncBalance().catch((e) =>
        console.warn('[EmbeddedMigration] CLOB sync failed (non-critical):', e)
      );

      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success(MESSAGES.TOAST.FUNDS_MOVED((usdceAmount + nativeAmount).toFixed(2)));
    } catch (error: any) {
      const msg = (error?.shortMessage || error?.message || '').toLowerCase();
      if (msg.includes('user rejected') || msg.includes('user denied')) {
        // User explicitly rejected — do nothing
      } else if (
        msg.includes('insufficient funds') ||
        msg.includes('insufficient balance') ||
        msg.includes('gas required exceeds')
      ) {
        toast.warn(MESSAGES.TOAST.WALLET_NEEDS_POL_GAS, { autoClose: 8000 });
      } else {
        console.error('[EmbeddedMigration] Failed to migrate funds:', error);
        // toast.error('Could not move funds to trading wallet. Please try again.');
      }
    } finally {
      isMigratingRef.current = false;
    }
  }, [user?.proxyWalletAddress, wallets, syncBalance, queryClient]);

  // Background polling: check embedded wallet every 30s so delayed deposits
  // from exchanges and card providers are automatically forwarded to proxy.
  // Exchange deposits can take 5–30 minutes to arrive on-chain.
  const isAuthenticated = !!user?.proxyWalletAddress;
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(() => {
      void migrateToProxy();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAuthenticated, migrateToProxy]);

  return { migrateToProxy };
}
